import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { WorkflowInputLayout } from './WorkflowInputLayout';
import EnvironmentalTab from '../../../components/utility_planning/scenarioInputs/EnvironmentalTab';
import { getMdhdWorkflowConfig, updateMdhdWorkflowConfig } from '../../../utils/mdhdWorkflowConfig';
import { getSelectedUtility } from '../../../utils/utilitySelection';
import { getUtilityWorkflowUiState } from '../../../utils/utilityWorkflowUiState';
import { API_ENDPOINTS } from '../../../config/utilityApiConfig';
import { isLocal } from '../../../config/env';

function getWorkflowSelectedUtility() {
  const persistedSelection = getSelectedUtility();
  const workflowUtilityName = getUtilityWorkflowUiState()?.mdhdAdoptionTrend?.selectedUtility || '';
  if (workflowUtilityName) {
    return {
      utilityName: workflowUtilityName,
      stateCode: persistedSelection?.stateCode || 'CA',
      utilityTerritoryId:
        persistedSelection?.utilityName === workflowUtilityName
          ? persistedSelection?.utilityTerritoryId || null
          : null,
    };
  }
  return persistedSelection;
}

export default function SeasonWeatherInputPage() {
  const [selectedUtility, setSelectedUtility] = useState(() => getWorkflowSelectedUtility());
  const [data, setData] = useState(() => ({
    weatherDatasetId: getMdhdWorkflowConfig().seasonWeather?.weatherDatasetId ?? null,
  }));
  const [weatherDiagnostics, setWeatherDiagnostics] = useState(null);
  const [weatherDiagnosticsLoading, setWeatherDiagnosticsLoading] = useState(false);
  const [weatherDiagnosticsError, setWeatherDiagnosticsError] = useState('');
  const updateData = (next) => setData((prev) => ({ ...prev, ...next }));
  const selectedUtilityName = selectedUtility?.utilityName || '';

  useEffect(() => {
    const refreshSelectedUtility = () => {
      setSelectedUtility(getWorkflowSelectedUtility());
    };
    refreshSelectedUtility();
    window.addEventListener('focus', refreshSelectedUtility);
    window.addEventListener('storage', refreshSelectedUtility);
    return () => {
      window.removeEventListener('focus', refreshSelectedUtility);
      window.removeEventListener('storage', refreshSelectedUtility);
    };
  }, []);

  const seasonalRows = useMemo(() => (
    weatherDiagnostics?.seasonal_summary
      ? ['winter', 'spring', 'summer', 'fall'].map((season) => ({
          season,
          ...(weatherDiagnostics.seasonal_summary?.[season] || {}),
        }))
      : []
  ), [weatherDiagnostics]);

  useEffect(() => {
    updateMdhdWorkflowConfig('seasonWeather', data);
  }, [data]);

  useEffect(() => {
    if (!isLocal || !selectedUtilityName) {
      setWeatherDiagnostics(null);
      setWeatherDiagnosticsError('');
      setWeatherDiagnosticsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchWeatherDiagnostics = async () => {
      setWeatherDiagnosticsLoading(true);
      setWeatherDiagnosticsError('');
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(API_ENDPOINTS.getWeatherDiagnostics, {
          params: { utility_name: selectedUtilityName, station_limit: 50 },
          headers: token ? { Authorization: `Token ${token}` } : {},
        });
        if (!cancelled) {
          setWeatherDiagnostics(response.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setWeatherDiagnostics(null);
          setWeatherDiagnosticsError(
            error?.response?.data?.error
            || error?.response?.data?.detail
            || error?.message
            || 'Failed to load weather diagnostics.'
          );
        }
      } finally {
        if (!cancelled) {
          setWeatherDiagnosticsLoading(false);
        }
      }
    };

    fetchWeatherDiagnostics();
    return () => {
      cancelled = true;
    };
  }, [selectedUtilityName]);

  return (
    <WorkflowInputLayout title="Season/weather">
      <div className="space-y-3">
        <div className="bg-white rounded-xl shadow-lg font-ui">
        <EnvironmentalTab
          data={data}
          updateData={updateData}
          utilityName={selectedUtility?.utilityName || ''}
          region={selectedUtility?.stateCode || 'CA'}
          weatherDiagnostics={weatherDiagnostics}
        />
        </div>
        {isLocal && (
          <div className="bg-white rounded-xl shadow-lg font-ui border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-800">Dev weather DB diagnostics</div>
              <div className="text-xs text-slate-500">{selectedUtilityName || 'No utility selected'}</div>
            </div>
            {weatherDiagnosticsLoading ? (
              <div className="text-sm text-slate-500">Loading weather diagnostics...</div>
            ) : weatherDiagnosticsError ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {weatherDiagnosticsError}
              </div>
            ) : !weatherDiagnostics ? (
              <div className="text-sm text-slate-500">No weather diagnostics available.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs uppercase text-slate-500">Raw rows</div>
                    <div className="text-lg font-semibold text-slate-800 mt-1">{weatherDiagnostics?.row_count ?? 0}</div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs uppercase text-slate-500">Raw stations in boundary</div>
                    <div className="text-lg font-semibold text-slate-800 mt-1">{weatherDiagnostics?.station_count ?? 0}</div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs uppercase text-slate-500">Nearest stations retained</div>
                    <div className="text-lg font-semibold text-slate-800 mt-1">{weatherDiagnostics?.resolved_station_count_for_utility ?? 0}</div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs uppercase text-slate-500">Observed max temp</div>
                    <div className="text-lg font-semibold text-slate-800 mt-1">
                      {weatherDiagnostics?.temperature?.max_temp_f == null ? '—' : `${Number(weatherDiagnostics.temperature.max_temp_f).toFixed(1)} F`}
                    </div>
                  </div>
                </div>

                {Array.isArray(weatherDiagnostics?.boundary_excluded_station_ids_preview)
                  && weatherDiagnostics.boundary_excluded_station_ids_preview.length > 0 && (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <span className="font-semibold">Nearby stations outside boundary</span> are shown as orange markers on the map. Their data is not included in the temperature aggregates above.
                      {' '}Stations:{' '}
                      <span className="font-mono">
                        {weatherDiagnostics.boundary_excluded_station_ids_preview.join(', ')}
                      </span>
                    </div>
                  )}

                <div className="overflow-x-auto rounded border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 uppercase">Season</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600 uppercase">Avg (F)</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600 uppercase">Min (F)</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600 uppercase">Observed Max (F)</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600 uppercase">Raw Rows</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {seasonalRows.map((row) => (
                        <tr key={`season-row-${row.season}`}>
                          <td className="px-3 py-2 text-slate-800 capitalize">{row.season}</td>
                          <td className="px-3 py-2 text-right text-slate-800">{Number(row.avg_temp_f ?? 0).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-slate-800">{Number(row.min_temp_f ?? 0).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-slate-800">{Number(row.max_temp_f ?? 0).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-slate-800">{row.row_count ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </WorkflowInputLayout>
  );
}
